import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close, CalendarMonth } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addDays } from 'date-fns';
import { useExtendDueDate } from '../../hooks/useInvoices';
import { Invoice } from '../../services/invoices';
import { getErrorInfo, ErrorInfo } from '../../services/api';
import ErrorMessage from '../ErrorMessage';

const extendDueDateSchema = z.object({
  dueDate: z.date({ required_error: 'New due date is required' }),
  reason: z.string().max(500).optional(),
});

type ExtendDueDateFormData = z.infer<typeof extendDueDateSchema>;

interface ExtendDueDateModalProps {
  open: boolean;
  invoice: Invoice | null;
  onClose: () => void;
}

export default function ExtendDueDateModal({ open, invoice, onClose }: ExtendDueDateModalProps) {
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const extendDueDate = useExtendDueDate();

  const currentDueDate = invoice ? new Date(invoice.dueDate) : new Date();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ExtendDueDateFormData>({
    resolver: zodResolver(extendDueDateSchema),
    defaultValues: {
      dueDate: addDays(currentDueDate, 7),
      reason: '',
    },
  });

  // Reset form when invoice changes
  useEffect(() => {
    if (invoice) {
      const dueDate = new Date(invoice.dueDate);
      reset({
        dueDate: addDays(dueDate, 7),
        reason: '',
      });
    }
  }, [invoice, reset]);

  const onSubmit = async (data: ExtendDueDateFormData) => {
    if (!invoice) return;
    setErrorInfo(null);

    try {
      await extendDueDate.mutateAsync({
        id: invoice._id,
        dueDate: data.dueDate.toISOString(),
        reason: data.reason,
      });

      reset();
      onClose();
    } catch (err: unknown) {
      const info = getErrorInfo(err);
      setErrorInfo({
        ...info,
        endpoint: `/invoices/${invoice._id}/due-date`,
        method: 'PATCH',
        payload: { dueDate: data.dueDate.toISOString(), reason: data.reason },
      });
    }
  };

  const handleClose = () => {
    reset();
    setErrorInfo(null);
    onClose();
  };

  const handleQuickExtend = (days: number) => {
    const newDate = addDays(currentDueDate, days);
    setValue('dueDate', newDate);
  };

  if (!invoice) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: '#DBEAFE',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#3B82F6',
                }}
              >
                <CalendarMonth />
              </Box>
              <Typography variant="h6">Extend Due Date</Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            {errorInfo && (
              <ErrorMessage
                error={errorInfo}
                userFriendlyMessage="Unable to update due date. Please try again."
                onClose={() => setErrorInfo(null)}
              />
            )}

            {/* Current Due Date Info */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Invoice {invoice.invoiceNumber}
              </Typography>
              <Typography variant="body1">
                Current due date: <strong>{format(currentDueDate, 'MMMM d, yyyy')}</strong>
              </Typography>
            </Box>

            {/* New Due Date */}
            <Controller
              name="dueDate"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="New Due Date"
                  value={field.value}
                  onChange={(newValue) => field.onChange(newValue)}
                  minDate={new Date()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: !!errors.dueDate,
                      helperText: errors.dueDate?.message,
                      sx: { mb: 2 },
                    },
                  }}
                />
              )}
            />

            {/* Quick extend buttons */}
            <Box display="flex" gap={1} mb={3} alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Quick extend:
              </Typography>
              {[7, 14, 30].map((days) => (
                <Button
                  key={days}
                  size="small"
                  variant="outlined"
                  onClick={() => handleQuickExtend(days)}
                >
                  +{days} days
                </Button>
              ))}
            </Box>

            {/* Reason (optional) */}
            <Controller
              name="reason"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Reason (optional)"
                  multiline
                  rows={2}
                  fullWidth
                  placeholder="e.g., Client requested extension due to cash flow"
                  helperText="This will be saved in internal notes"
                  error={!!errors.reason}
                />
              )}
            />
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Due Date'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
}
